import React, { useRef, useEffect, useCallback } from 'react'

import { isMobile } from '../../../global/browser'
import { useQueryString } from '../../../sharedLegacy/hooks'
import {
  JoinToSpeakProvider,
  JoinToSpeakRole,
  useHasJoinedToSpeak,
} from '../../../sharedLegacy/join-to-speak'
import useFeatures from '../../../sharedLegacy/hooks/useFeatures'
import usePreconnect from '../../hooks/usePreconnect'
import AudienceFoyer from '../AudienceFoyerComponent/AudienceFoyerComponent'
import WebinarAudienceRoomContainer from '../WebinarAudienceRoomContainerComponent/WebinarAudienceRoomContainerComponent'
import { useAudienceAppState } from '../../providers/AudienceAppStateProvider'
import { ConnectingScreen } from '../../../Webinar/components/ConnectingScreen/ConnectingScreen'
import { useWebinarDesignQuery } from '../../../Designs/blocs/useWebinarDesign'

import AudienceFoyerOptions from './AudienceFoyerOptionsComponent'

const playSilence = () => {
  const silence = document.getElementById('silence')
  if (silence) {
    silence.play()
  }
}

const AudienceAppComponent = () => {
  const { setError, scope, setScope, liveId, liveToken } = useAudienceAppState()
  const joiningToSpeakTimeoutRef = useRef(null)
  const { load } = usePreconnect(liveId, liveToken, setError)
  const [customizeQueryParam] = useQueryString('customize')
  const [fastForwardQueryParam] = useQueryString('ff')
  const { HasJoinToSpeakEnabled } = useFeatures()
  const { data: webinarExperience } = useWebinarDesignQuery()
  const [hasJoinedToSpeak, setHasJoinedToSpeak] = useHasJoinedToSpeak(liveId)

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (hasJoinedToSpeak) {
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (joiningToSpeakTimeoutRef.current !== null) {
        clearTimeout(joiningToSpeakTimeoutRef.current)
      }

      const timeout = setTimeout(() => {
        setHasJoinedToSpeak(false)
        joiningToSpeakTimeoutRef.current = null
      }, 1000)

      joiningToSpeakTimeoutRef.current = timeout
    }
  }, [hasJoinedToSpeak, setHasJoinedToSpeak])

  useEffect(() => {
    /**
     * Hack borrowed from the original audience app room.
     */
    document.body.classList.add('react-audience-room')

    /**
     * We are intentionally skipping the foyer on mobile, for now. We set the
     *  scope to audience like we would if they went through the foyer.
     */
    if (isMobile()) {
      setScope('audience')
    }
  }, [setScope])

  /**
   * Should be able to hide the foyer when you are in room customization mode.
   *  Right now the scope of the application is set to customize, but since the
   *  scope really isn't used to anything yet in the audience application, it's
   *  just for identifing the scope priviledges for good measure.
   */
  useEffect(() => {
    if (customizeQueryParam) {
      setScope('customize')
    }
  }, [customizeQueryParam, setScope])

  useEffect(() => {
    if (fastForwardQueryParam) {
      setScope('audience')
    }
  }, [fastForwardQueryParam, setScope])

  const onOptionClick = useCallback(
    (option) => () => {
      /**
       * Whenever an audience member selects an option, we make sure to play a
       *  silent video tricking the browser into believing that we want to
       *  receive audio and that way the webinar stream will start unmuted.
       */
      playSilence()
      setScope(option)
    },
    [setScope]
  )

  if (!webinarExperience) return null

  if (hasJoinedToSpeak) {
    return <ConnectingScreen text="We are taking you back to the audience" />
  }

  if (!scope) {
    return (
      <AudienceFoyer
        onError={setError}
        renderOptions={(name, link) => (
          <AudienceFoyerOptions
            onOptionClick={onOptionClick}
            fromLink={link}
            name={name}
          />
        )}
      />
    )
  }

  const joinToSpeakRole = ['audience', 'customize'].includes(scope)
    ? JoinToSpeakRole.Audience
    : null

  return (
    <JoinToSpeakProvider
      isEnabled={HasJoinToSpeakEnabled}
      role={joinToSpeakRole}
      {...(joinToSpeakRole === JoinToSpeakRole.Audience
        ? { sessionId: liveId, token: liveToken }
        : { sessionId: liveId })}
    >
      <WebinarAudienceRoomContainer />
    </JoinToSpeakProvider>
  )
}

export { AudienceAppComponent }
